import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Footer from '@/components/feature/Footer';
import { getMyActivities, getMyParticipations } from '@/services/activityService';
import { apiActivityToFront } from '@/hooks/useFeed';
import type { Activity } from '@/lib/types';
import MyActivityCard from './components/MyActivityCard';
import logoYonyonHeader from '@/assets/logo-yonyon-header-300x100.png';

type Tab = 'joined' | 'created';

export default function MyActivities() {
  const { user, removeCreatedActivity } = useAuth();
  const [tab, setTab] = useState<Tab>('joined');

  const [joinedActivities, setJoinedActivities] = useState<Activity[]>([]);
  const [createdActivities, setCreatedActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [participations, created] = await Promise.allSettled([
        getMyParticipations(),
        getMyActivities(),
      ]);
      const joined = participations.status === 'fulfilled'
        ? participations.value.map(apiActivityToFront)
        : [];
      const mine = created.status === 'fulfilled'
        ? created.value.map(apiActivityToFront)
        : [];
      setJoinedActivities(joined);
      setCreatedActivities(mine);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur de chargement';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteCreated = async (id: string) => {
    setCreatedActivities((prev) => prev.filter((a) => a.id !== id));
    await removeCreatedActivity(id);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <TopBar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-sm">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
              <i className="ri-lock-line text-2xl"></i>
            </div>
            <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">
              Connecte-toi pour voir tes activités
            </h1>
            <p className="mt-1 text-sm text-foreground-500">
              Tes inscriptions et créations apparaîtront ici.
            </p>
            <Link
              to="/auth"
              className="mt-5 inline-block px-4 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <TopBar />

      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-primary-500 text-white flex items-center justify-center text-base font-bold">
              {user.name
                .split(' ')
                .map((p) => p[0])
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </span>
            <div>
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                Mes activités
              </h1>
              <p className="text-sm text-foreground-500">
                {user.name}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-6 inline-flex rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setTab('joined')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${tab === 'joined'
                ? 'bg-white text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-700'
                }`}
            >
              Rejointes ({joinedActivities.length})
            </button>
            <button
              type="button"
              onClick={() => setTab('created')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${tab === 'created'
                ? 'bg-white text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-700'
                }`}
            >
              Créées ({createdActivities.length})
            </button>
          </div>

          {/* Content */}
          <div className="mt-6 flex flex-col gap-4">
            {loading ? (
              [1, 2].map((i) => (
                <div key={i} className="bg-white border border-background-200 rounded-lg p-5 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-background-100 shrink-0"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-background-100 rounded w-24 mb-2"></div>
                      <div className="h-5 bg-background-100 rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : loadError ? (
              <div className="bg-white border border-background-200 rounded-lg p-8 text-center">
                <p className="text-sm text-foreground-500">{loadError}</p>
                <button
                  type="button"
                  onClick={loadData}
                  className="mt-3 px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Réessayer
                </button>
              </div>
            ) : tab === 'joined' ? (
              joinedActivities.length === 0 ? (
                <EmptyState
                  icon="ri-compass-3-line"
                  title="Aucune activité rejointe"
                  text="Explore le fil et rejoins une sortie, un sport ou un coup de main."
                  cta={{ to: '/', label: 'Explorer les activités' }}
                />
              ) : (
                joinedActivities.map((joinedActivity) => (
                  <MyActivityCard key={joinedActivity.id} activity={joinedActivity} />
                ))
              )
            ) : createdActivities.length === 0 ? (
              <EmptyState
                icon="ri-add-circle-line"
                title="Tu n'as rien créé pour l'instant"
                text="Propose une activité et fais venir des gens près de chez toi."
                cta={{ to: '/', label: 'Créer une activité' }}
              />
            ) : (
              createdActivities.map((a) => (
                <MyActivityCard
                  key={a.id}
                  activity={a}
                  created
                  onDelete={() => handleDeleteCreated(a.id)}
                />
              ))
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function TopBar() {
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-background-200">
      <div className="px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        <div className="md:px-8 h-16 flex items-center justify-between gap-4 w-full">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Retour
          </Link>
          <Link
            to="/"
            className="
            block overflow-hidden
            lg:flex lg:overflow-visible
            cursor-pointer bg-white rounded-lg px-2 py-1
          "
          >
            <img
              src={logoYonyonHeader}
              alt="Yonyon"
              className="h-14 w-auto translate-x-[3rem] lg:translate-x-0"
            />
          </Link>
        </div>
      </div>
    </header>
  );
}

function EmptyState({
  icon,
  title,
  text,
  cta,
}: {
  icon: string;
  title: string;
  text: string;
  cta: { to: string; label: string };
}) {
  return (
    <div className="bg-white border border-background-200 rounded-lg p-10 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
        <i className={`${icon} text-2xl`}></i>
      </div>
      <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
        {title}
      </h2>
      <p className="mt-1 text-sm text-foreground-500 max-w-xs mx-auto">{text}</p>
      <Link
        to={cta.to}
        className="mt-4 inline-block px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        {cta.label}
      </Link>
    </div>
  );
}